import { Loadable } from '@/components/Loadable';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import { Dialog, Icons, Sheet } from '@packages/webshop-design-system';
import { type FC, type ReactNode } from 'react';
import { useMediaQuery } from 'react-responsive';

interface ResponsiveDialogProps {
  open: boolean;
  onOpenChange: (isOpen: boolean) => void;
  loading?: boolean;
  trigger?: ReactNode;
  title: ReactNode;
  content: ReactNode;
  footer?: ReactNode;
  className?: {
    root?: string;
    content?: string;
    footer?: string;
    loadable?: string;
  };
}

const ResponsiveDialog: FC<ResponsiveDialogProps> = ({
  open,
  onOpenChange,
  loading = false,
  trigger,
  title,
  content,
  footer,
  className,
}) => {
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  if (isLaptopOrBigger) {
    return (
      <Dialog
        isOpen={open}
        onOpenChange={onOpenChange}
        trigger={trigger}
        headerTitle={title}
        footer={footer}
        className={{
          root: cn('w-[650px] max-w-none', className?.root),
          content: cn('p-0', className?.content),
          footer: className?.footer,
        }}
        data-testid="responsive-dialog"
      >
        <Loadable
          loading={loading}
          persistent
          className={{
            persistent: cn(
              className?.loadable == null && 'min-h-[500px]',
              className?.loadable
            ),
          }}
        >
          {content}
        </Loadable>
      </Dialog>
    );
  }

  return (
    <Sheet
      isOpen={open}
      onOpenChange={onOpenChange}
      trigger={trigger}
      headerTitle={title}
      closeIcon={<Icons.arrowLeft height={24} width={24} />}
      footer={footer}
      className={{
        root: cn('min-w-full', className?.root),
        content: cn('p-0', className?.content),
        footer: className?.footer,
      }}
      data-testid="responsive-dialog-sheet"
    >
      <Loadable
        loading={loading}
        persistent
        className={{
          persistent: cn('flex-1 overflow-auto', className?.loadable),
        }}
      >
        {content}
      </Loadable>
    </Sheet>
  );
};

export default ResponsiveDialog;
